import { defineMessages } from 'react-intl';

type MessageKeys =
	| 'backButtonLabel'
	| 'headerTitle'
	| 'newChatButtonLabel'
	| 'closeChatButtonLabel'
	| 'noResults'
	| 'searchPlaceholder'
	| 'accessibleSearchResults';

const message: Record<MessageKeys, { id: string; defaultMessage: string; description?: string }> =
	defineMessages({
		backButtonLabel: {
			id: 'ai-mate.conversation-list-view.back-button.label',
			defaultMessage: 'Back',
			description:
				'Button label displayed on the back button in the chat conversation list view within the AI conversation interface. Shown as the text on a button. When clicked, it navigates back to the previous view or closes the conversation list. The purpose is to provide users with a way to return to the previous screen or exit the conversation list view.',
		},
		headerTitle: {
			id: 'ai-mate.conversation-list-view.header-title',
			defaultMessage: 'Chat History',
			description:
				'Title text displayed at the top of the chat conversation list view within the AI conversation interface. Shown as the header title of the conversation list. The purpose is to immediately communicate to the user what view they are in, which displays their past conversation history with the AI assistant.',
		},
		newChatButtonLabel: {
			id: 'ai-mate.conversation-list-view.new-chat-button.label',
			defaultMessage: 'New chat',
			description:
				'Button label displayed on the new chat button in the chat conversation list view within the AI conversation interface. Shown as the text on a button. When clicked, it starts a new conversation session, clearing the current chat history. The purpose is to provide users with a clear way to begin a fresh conversation with the AI assistant from the conversation list view.',
		},
		closeChatButtonLabel: {
			id: 'ai-mate.conversation-list-view.close-chat-button.label',
			defaultMessage: 'Close chat',
			description:
				'Button label displayed on the close chat button in the chat conversation list view within the AI conversation interface. Shown as the text on a button. When clicked, it closes the current chat view and returns to the conversation list or main interface. The purpose is to provide users with a way to exit the current chat session and return to the list of conversations.',
		},
		noResults: {
			id: 'ai-mate.conversation-list-view.no-chat',
			defaultMessage: 'No results',
			description:
				'Empty state message displayed in the chat conversation list view within the AI conversation interface when there are no conversations to display, either because none exist or because a search returned no matches. Shown as text in an empty state component. The purpose is to inform users that no conversations are available to display.',
		},
		searchPlaceholder: {
			id: 'ai-mate.conversation-list-view.search-input.placeholder',
			defaultMessage: 'Search chats',
			description:
				'Placeholder text displayed in a search input field in the chat conversation list view within the AI conversation interface. Shown as placeholder text when the input field is empty. The purpose is to guide users on what they should enter in the search field, prompting them to search for conversations by name or content.',
		},
		accessibleSearchResults: {
			id: 'ai-mate.conversation-list-view.accessible-search-results',
			// eslint-disable-next-line @atlassian/i18n/enforce-plural-rules
			defaultMessage: '{results, plural, =1 {1 result found} other {# results found}}',
			description:
				'Accessibility announcement text displayed or announced by screen readers in the chat conversation list view within the AI conversation interface when search results are updated. Shown as a screen reader announcement or status message. The results placeholder will be substituted with the number of search results found. The ICU MessageFormat plural statement resolves to "1 result found" when count is 1, or "# results found" when count is 2 or more (where # is the count). The purpose is to provide accessible feedback to users with assistive technologies about how many conversations match their search query.',
		},
	});

export default message;
